import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  ChangeDetectionStrategy,
  Output,
  EventEmitter,
  Input,
  input,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { AemsFile } from '../../../models/aems-file.model';
import { FileViewerComponent } from '../file-viewer/file-viewer.component';
import { FilePreviewComponent } from '../file-preview/file-preview.component';
import { signal, computed, effect } from '@angular/core';

@Component({
  selector: 'app-file-select',
  imports: [FileViewerComponent, FilePreviewComponent],
  templateUrl: './file-select.component.html',
  styleUrls: ['./file-select.component.scss'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FileSelectComponent implements OnChanges {
  @Input() readOnly: boolean = false;
  @Input() resetTrigger: number = 0;
  @Input() lastConfirmedState: Map<string, boolean> = new Map(); // Add this input
  files = input.required<AemsFile[]>();
  @Output() fileLinked = new EventEmitter<AemsFile[]>();

  private _selectedFile: AemsFile | null = null;
  private _isResetting: boolean = false;

  ngOnChanges(changes: SimpleChanges) {
    if (
      changes['resetTrigger'] &&
      changes['resetTrigger'].currentValue !== changes['resetTrigger'].previousValue &&
      this.resetTrigger > 0
    ) {
      this.resetFileStates();
    }
  }

  private _fileStates = signal<Map<string, boolean>>(new Map());

  private initializeFileStates = effect(() => {
    const files = this.files();

    if (this._isResetting) {
      return;
    }

    if (files && files.length > 0) {
      const currentStates = this._fileStates();

      // Only initialize if we have no states at all, or if there are completely new files
      const shouldInitialize = currentStates.size === 0;

      if (shouldInitialize) {
        const newStates = new Map<string, boolean>();
        files.forEach((file) => {
          newStates.set(file.generatedName, file.linkFile ?? false);
        });
        this._fileStates.set(newStates);
      } else {
        // Handle new files only - don't override existing states
        const newStates = new Map(currentStates);
        let hasNewFiles = false;

        files.forEach((file) => {
          if (!newStates.has(file.generatedName)) {
            newStates.set(file.generatedName, file.linkFile ?? false);
            hasNewFiles = true;
          }
        });

        if (hasNewFiles) {
          this._fileStates.set(newStates);
        }
      }
    }
  });

  filesWithStates = computed(() => {
    const files = this.files();
    const states = this._fileStates();

    return files.map((file) => ({
      ...file,
      linkFile: states.get(file.generatedName) ?? file.linkFile,
    }));
  });

  openFileViewer: boolean = false;
  navDisplayTextForFileViewer = '';
  disablePrevious: boolean | null = null;
  disableNext: boolean | null = null;
  linkFileMessage: string = 'Link image to the repair line item.';

  get selectedFile(): AemsFile | null {
    if (!this._selectedFile) return null;

    const files = this.filesWithStates();
    const currentFile = files.find((f) => f.generatedName === this._selectedFile!.generatedName);

    return currentFile || null;
  }

  onThumbnailClicked(file: AemsFile) {
    const files = this.filesWithStates();
    const fileIndex = files.findIndex((f) => f.generatedName === file.generatedName);

    if (fileIndex !== -1) {
      this._selectedFile = files[fileIndex];
    }

    this.openFileViewer = true;
    let currentIndex = files.findIndex((f) => f.generatedName === file.generatedName);
    this.navDisplayTextForFileViewer = `${currentIndex + 1}/${files.length}`;

    if (currentIndex == 0) {
      this.disablePrevious = true;
    }
    if (currentIndex == files.length - 1) {
      this.disableNext = true;
    }
  }

  onFileLinkedChange(file: AemsFile) {
    const currentStates = this._fileStates();

    const newStates = new Map(currentStates);
    newStates.set(file.generatedName, file.linkFile ?? false);

    this._fileStates.set(newStates);

    // Emit updated files
    this.fileLinked.emit(this.filesWithStates());
  }

  onNextClicked() {
    const files = this.filesWithStates();
    const currentSelected = this.selectedFile;
    if (!currentSelected) return;

    let currentIndex = files.findIndex((f) => f.generatedName === currentSelected.generatedName);
    let nextIndex = currentIndex + 1;

    if (nextIndex < files.length) {
      this._selectedFile = files[nextIndex];
    }

    if (nextIndex == files.length - 1) {
      this.disableNext = true;
    }
    if (this.disablePrevious) {
      this.disablePrevious = null;
    }
    this.navDisplayTextForFileViewer = `${nextIndex + 1}/${files.length}`;
  }

  onPreviousClicked() {
    const files = this.filesWithStates();
    const currentSelected = this.selectedFile;
    if (!currentSelected) return;

    let currentIndex = files.findIndex((f) => f.generatedName === currentSelected.generatedName);
    let prevIndex = currentIndex - 1;

    if (prevIndex >= 0) {
      this._selectedFile = files[prevIndex];
    }

    if (prevIndex == 0) {
      this.disablePrevious = true;
    }
    if (this.disableNext) {
      this.disableNext = null;
    }
    this.navDisplayTextForFileViewer = `${prevIndex + 1}/${files.length}`;
  }

  onFileViewerClosed() {
    this.disableNext = false;
    this.disablePrevious = false;
    this.openFileViewer = false;
  }

  private resetFileStates() {
    this._isResetting = true;

    const files = this.files();
    if (files && files.length > 0) {
      // Create a completely new Map using last confirmed state instead of original values
      const resetStates = new Map<string, boolean>();
      files.forEach((file) => {
        // Use last confirmed state if available, otherwise fall back to original
        const lastConfirmedValue = this.lastConfirmedState.get(file.generatedName);
        const resetValue = lastConfirmedValue !== undefined ? lastConfirmedValue : (file.linkFile ?? false);

        resetStates.set(file.generatedName, resetValue);
      });

      // Force update the signal with new Map
      this._fileStates.set(resetStates);

      // Emit the reset state immediately
      this.fileLinked.emit(this.filesWithStates());
    }

    // Reset the flag after a longer delay to ensure everything is processed
    setTimeout(() => {
      this._isResetting = false;
    }, 300);
  }
}
